import { z } from 'zod';
// ============= API Route Types =============
/**
 * HTTP methods.
 */
export const HttpMethod = z.enum([
    'GET',
    'POST',
    'PUT',
    'PATCH',
    'DELETE',
    'HEAD',
    'OPTIONS',
    'WS',
    'SSE',
]);
/**
 * Web framework types.
 */
export const WebFramework = z.enum([
    'express',
    'fastify',
    'nestjs',
    'koa',
    'hapi',
    'fastapi',
    'flask',
    'django',
    'spring',
    'rails',
    'laravel',
    'gin',
    'echo',
    'unknown',
]);
/**
 * Authentication types.
 */
export const AuthType = z.enum([
    'none',
    'jwt',
    'oauth',
    'api-key',
    'basic',
    'session',
    'unknown',
]);
/**
 * API endpoint parameter.
 */
export const RouteParamSchema = z.object({
    name: z.string(),
    type: z.string().optional(),
    required: z.boolean().default(true),
    location: z.enum(['path', 'query', 'body', 'header']),
});
/**
 * API endpoint definition.
 */
export const ApiEndpointSchema = z.object({
    id: z.string(),
    method: HttpMethod,
    path: z.string(),
    handler: z.string().optional(),
    description: z.string().optional(),
    params: z.array(RouteParamSchema).optional(),
    auth: AuthType.optional(),
    tags: z.array(z.string()).optional(),
    middleware: z.array(z.string()).optional(),
    responseType: z.string().optional(),
    sourceFile: z.string().optional(),
    sourceLine: z.number().optional(),
});
/**
 * API namespace/resource group.
 */
export const ApiNamespaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    basePath: z.string(),
    description: z.string().optional(),
    endpoints: z.array(z.string()), // Endpoint IDs
    auth: AuthType.optional(),
});
/**
 * Complete API route map diagram data.
 */
export const ApiRouteDiagramDataSchema = z.object({
    metadata: z.object({
        title: z.string(),
        description: z.string().optional(),
        framework: WebFramework,
        baseUrl: z.string().optional(),
        version: z.string().optional(),
        sourceFiles: z.array(z.string()).optional(),
        generatedAt: z.date().optional(),
    }),
    namespaces: z.array(ApiNamespaceSchema),
    endpoints: z.array(ApiEndpointSchema),
});
// ============= Helper Functions =============
/**
 * Get HTTP method color.
 */
export function getMethodColor(method) {
    switch (method) {
        case 'GET':
            return '#22c55e'; // Green
        case 'POST':
            return '#3b82f6'; // Blue
        case 'PUT':
            return '#f59e0b'; // Amber
        case 'PATCH':
            return '#8b5cf6'; // Purple
        case 'DELETE':
            return '#ef4444'; // Red
        case 'HEAD':
            return '#64748b'; // Slate
        case 'OPTIONS':
            return '#06b6d4'; // Cyan
        case 'WS':
            return '#a855f7'; // Purple
        case 'SSE':
            return '#f97316'; // Orange
        default:
            return '#64748b';
    }
}
/**
 * Get method badge background color (light).
 */
export function getMethodBgColor(method) {
    switch (method) {
        case 'GET':
            return '#dcfce7';
        case 'POST':
            return '#dbeafe';
        case 'PUT':
            return '#fef3c7';
        case 'PATCH':
            return '#f3e8ff';
        case 'DELETE':
            return '#fee2e2';
        case 'HEAD':
            return '#f1f5f9';
        case 'OPTIONS':
            return '#cffafe';
        case 'WS':
            return '#f3e8ff';
        case 'SSE':
            return '#ffedd5';
        default:
            return '#f1f5f9';
    }
}
/**
 * Extract resource name from path.
 */
export function extractResourceFromPath(path) {
    // Remove leading slash and split
    const parts = path.replace(/^\//, '').split('/');
    // Find the first non-parameter segment
    for (const part of parts) {
        if (!part.startsWith(':') && !part.startsWith('{') && part !== 'api' && part !== 'v1' && part !== 'v2') {
            return part;
        }
    }
    return parts[0] || 'root';
}
/**
 * Group endpoints by resource path.
 */
export function groupEndpointsByResource(endpoints) {
    const groups = new Map();
    for (const endpoint of endpoints) {
        const resource = extractResourceFromPath(endpoint.path);
        const existing = groups.get(resource) || [];
        existing.push(endpoint);
        groups.set(resource, existing);
    }
    return groups;
}
/**
 * Check if path is protected (requires auth).
 */
export function inferAuthFromPath(path, handler) {
    const lower = (path + (handler || '')).toLowerCase();
    if (lower.includes('public') || lower.includes('health') || lower.includes('ping')) {
        return 'none';
    }
    if (lower.includes('auth') || lower.includes('login') || lower.includes('register')) {
        return 'none'; // Auth endpoints themselves are public
    }
    if (lower.includes('admin')) {
        return 'jwt'; // Admin routes usually need auth
    }
    return 'unknown';
}
/**
 * Convert ApiRouteDiagramData to UnifiedDiagramData.
 */
export function apiRoutesToUnifiedDiagram(data) {
    const nodes = [];
    const edges = [];
    // Create namespace nodes
    let xOffset = 0;
    const namespacePositions = new Map();
    for (const namespace of data.namespaces) {
        namespacePositions.set(namespace.id, { x: xOffset, y: 0 });
        xOffset += 350;
    }
    // Create endpoint nodes grouped by namespace
    for (const namespace of data.namespaces) {
        const pos = namespacePositions.get(namespace.id) || { x: 0, y: 0 };
        const nsEndpoints = data.endpoints.filter(e => namespace.endpoints.includes(e.id));
        // Create namespace group node
        nodes.push({
            id: namespace.id,
            type: 'gateway',
            label: namespace.name,
            description: namespace.basePath,
            position: { x: pos.x, y: pos.y },
            data: {
                basePath: namespace.basePath,
                endpointCount: nsEndpoints.length,
            },
        });
        // Create endpoint nodes
        let yOffset = 100;
        for (const endpoint of nsEndpoints) {
            nodes.push({
                id: endpoint.id,
                type: 'service',
                label: `${endpoint.method} ${endpoint.path}`,
                description: endpoint.description,
                position: { x: pos.x, y: yOffset },
                data: {
                    method: endpoint.method,
                    path: endpoint.path,
                    handler: endpoint.handler,
                    auth: endpoint.auth,
                },
            });
            // Edge from namespace to endpoint
            edges.push({
                id: `${namespace.id}-${endpoint.id}`,
                source: namespace.id,
                target: endpoint.id,
                type: 'default',
                animated: false,
            });
            yOffset += 80;
        }
    }
    return {
        metadata: {
            title: data.metadata.title,
            description: data.metadata.description,
            diagramType: 'apiMap',
            category: 'architecture',
            layoutDirection: 'TB',
            layoutAlgorithm: 'dagre',
            sourceFiles: data.metadata.sourceFiles,
            generatedAt: data.metadata.generatedAt,
            generatedBy: 'regex',
        },
        nodes,
        edges,
    };
}
//# sourceMappingURL=apiRoutes.js.map